import { setOssSession } from '@/utils/ossImageAccess'
import { defineStore } from 'pinia'
import { store } from '../index'
import { UserLoginType, UserType, AdminAuthSession } from '@/api/login/types'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { loginOutApi } from '@/api/login'
import { useTagsViewStore } from './tagsView'
import { useLockStore } from './lock'
import router from '@/router'
import { ADMIN_SESSION_KEY, readAdminSession } from '@/utils/adminSession'

interface UserState {
  tokenKey: string
  session: AdminAuthSession | null
  reloadRequired: boolean
  roleRouters?: string[] | AppCustomRouteRecordRaw[]
  rememberMe: boolean
  loginInfo?: UserLoginType
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      tokenKey: 'Authorization',
      session: readAdminSession(),
      reloadRequired: false,
      roleRouters: undefined,
      // 记住我
      rememberMe: true,
      loginInfo: undefined
    }
  },
  getters: {
    getTokenKey(): string {
      return this.tokenKey
    },
    getToken(): string {
      return this.session?.token || ''
    },
    getUserInfo(): UserType | undefined {
      return this.session?.user
    },
    getSessionIdentity(): string {
      return this.session?.sessionId || ''
    },
    getRoleRouters(): string[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters
    },
    getRememberMe(): boolean {
      return this.rememberMe
    },
    getLoginInfo(): UserLoginType | undefined {
      return this.loginInfo
    }
  },
  actions: {
    setTokenKey(tokenKey: string) {
      this.tokenKey = tokenKey
    },
    setSession(session: AdminAuthSession) {
      localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session))
      setOssSession(session.sessionId)
      this.session = session
    },
    setRoleRouters(roleRouters: string[] | AppCustomRouteRecordRaw[]) {
      this.roleRouters = roleRouters
    },
    logoutConfirm() {
      const { t } = useI18n()
      ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
        confirmButtonText: t('common.ok'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      })
        .then(async () => {
          try {
            await loginOutApi()
          } catch (error) {
            ElMessage.error(error instanceof Error ? error.message : '退出失败，请稍后重试')
          }
        })
        .catch(() => {})
    },
    reset() {
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()
      localStorage.removeItem(ADMIN_SESSION_KEY)
      setOssSession('')
      this.session = null
      useLockStore().resetLockInfo()
      this.setRoleRouters([])
      router.replace('/login')
    },
    logout() {
      this.reset()
    },
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
    },
    setLoginInfo(loginInfo: UserLoginType | undefined) {
      this.loginInfo = loginInfo
    }
  },
  // 凭据单独原子存储，避免其他标签页保存偏好时覆盖正在轮换的 token。
  persist: { pick: ['tokenKey', 'roleRouters', 'rememberMe', 'loginInfo'] }
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
